// I18nField: tabbed input (ES/EN/FR) for translatable strings.
// - value = { es, en, fr } or plain string (legacy)
// - onChange(newDict) always with { es, en, fr }
// - Button "Traducir con IA" fills EN/FR from ES via /api/admin/translate

import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { LANGS, LANG_META } from '../../lib/i18n';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const EMPTY = { es: '', en: '', fr: '' };

function normalize(v) {
  if (v === null || v === undefined) return { ...EMPTY };
  if (typeof v === 'string') return { es: v, en: '', fr: '' };
  return { es: v.es || '', en: v.en || '', fr: v.fr || '' };
}

export default function I18nField({ value, onChange, type = 'input', rows = 3, placeholder = '', className = '', testId = 'i18n-field' }) {
  const val = normalize(value);
  const [active, setActive] = useState('es');
  const [translating, setTranslating] = useState(null);
  const { toast } = useToast();

  const set = (lang, str) => onChange({ ...val, [lang]: str });

  const translate = async (target) => {
    if (!val.es || !val.es.trim()) {
      toast({ title: 'Escribe primero el texto en español', variant: 'destructive' });
      return;
    }
    try {
      setTranslating(target);
      const { data } = await api.post('/admin/translate', { text: val.es, target_lang: target, source_lang: 'es' });
      set(target, data.translated || '');
      setActive(target);
      toast({ title: `Traducido a ${LANG_META[target].label}` });
    } catch (e) {
      toast({ title: 'Traducción falló', description: e.response?.data?.detail || 'Error del servicio', variant: 'destructive' });
    } finally {
      setTranslating(null);
    }
  };

  const baseInputCls = 'bg-zinc-950 border border-white/10 text-white/90 px-3 py-2 text-sm focus:border-[#c9a063] focus:outline-none w-full';

  return (
    <div className={`border border-white/10 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between bg-black/40 px-2 py-1.5 border-b border-white/10">
        <div className="flex gap-1">
          {LANGS.map((code) => {
            const m = LANG_META[code];
            const hasContent = val[code] && val[code].trim();
            return (
              <button
                key={code}
                type="button"
                onClick={() => setActive(code)}
                className={`text-[10px] uppercase tracking-widest px-2.5 py-1 flex items-center gap-1.5 border ${active === code ? 'border-[#c9a063] text-[#c9a063] bg-[#c9a063]/10' : 'border-transparent text-white/50 hover:text-white/80'}`}
                data-testid={`${testId}-tab-${code}`}
              >
                <span aria-hidden>{m.flag}</span>
                <span>{m.short}</span>
                {hasContent && <span className="w-1 h-1 rounded-full bg-emerald-400" aria-hidden />}
              </button>
            );
          })}
        </div>
        {active !== 'es' && (
          <button
            type="button"
            onClick={() => translate(active)}
            disabled={translating === active}
            className="text-[10px] uppercase tracking-widest text-[#c9a063] hover:text-white inline-flex items-center gap-1 px-2 py-1 disabled:opacity-50"
            title="Traducir desde español con IA"
            data-testid={`${testId}-translate-${active}`}
          >
            {translating === active ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            Traducir con IA
          </button>
        )}
      </div>
      <div className="p-2 bg-zinc-950">
        {type === 'input' ? (
          <input
            value={val[active]}
            onChange={(e) => set(active, e.target.value)}
            placeholder={placeholder}
            className={baseInputCls + ' border-transparent focus:border-[#c9a063]'}
            data-testid={`${testId}-input-${active}`}
          />
        ) : (
          <textarea
            rows={rows}
            value={val[active]}
            onChange={(e) => set(active, e.target.value)}
            placeholder={placeholder}
            className={baseInputCls + ' resize-none border-transparent focus:border-[#c9a063]'}
            data-testid={`${testId}-textarea-${active}`}
          />
        )}
      </div>
    </div>
  );
}
